/**
 * Responsibility: the fields that narrow a search, and nothing about where.
 *
 * Was `SearchFilterRow`, laid out across the page. Same fields, same
 * reasoning behind each; it is the container that changed, so this takes a
 * draft and a setter and lets the popover decide the arrangement.
 *
 * Two rows on purpose, split by what they do: the first picks a bucket, the
 * second bounds a number or a date. Ranges are open-ended because one end is
 * usually the whole question - "anything over $500".
 */
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { MarketCategory } from '../../api/client';
import type { SearchDraft } from './searchDraft';

/**
 * Shown under the fields, and chosen to teach the two things that actually
 * change the results.
 *
 * The catalogue indexes the name as printed on the card, so "stephen curry"
 * finds the autographed rookies and "steph curry" finds none of them. And it
 * knows the word "autograph" but not "signed" - measured: "stephen curry
 * autograph rookie" returns a PSA 10 at $22,465, while "steph curry signed"
 * returns ordinary base cards.
 */
const WORDING_HINT =
  'Use the name as printed - "stephen curry", not "steph" - and say "autograph" rather than "signed". The catalogue indexes both literally.';

type SearchFilterFieldsProps = {
  draft: SearchDraft;
  onChange: (next: SearchDraft) => void;
};

export function SearchFilterFields({ draft, onChange }: SearchFilterFieldsProps) {
  const set = <K extends keyof SearchDraft>(key: K, value: SearchDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const digits = (value: string) => value.replace(/[^0-9.]/g, '');
  const year = (value: string) => value.replace(/[^0-9]/g, '').slice(0, 4);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Catalogue"
          value={draft.category}
          onChange={(event) => set('category', event.target.value as MarketCategory | '')}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Everything</MenuItem>
          <MenuItem value="SPORTS">Sports</MenuItem>
          <MenuItem value="TCG">Trading cards</MenuItem>
        </TextField>

        <TextField
          size="small"
          label="Set contains"
          value={draft.setContains}
          onChange={(event) => set('setContains', event.target.value)}
          sx={{ width: 190 }}
          placeholder="mosaic, base set"
        />

        {/* Graded prices are missing for most of racing and soccer, so this
            is the difference between an arbitrage board and a page of
            blanks. */}
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={draft.gradedOnly}
              onChange={(event) => set('gradedOnly', event.target.checked)}
            />
          }
          label={<Typography variant="body2">Graded price only</Typography>}
        />
      </Stack>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="Max raw cost"
          value={draft.maxRaw}
          onChange={(event) => set('maxRaw', digits(event.target.value))}
          sx={{ width: 140 }}
          slotProps={{
            input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
          }}
        />

        <TextField
          size="small"
          label="Min value"
          value={draft.minValue}
          onChange={(event) => set('minValue', digits(event.target.value))}
          sx={{ width: 140 }}
          slotProps={{
            input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
          }}
        />

        {/* Sports sets *are* their year - 2020 Mosaic and 2019 Mosaic are
            different products - so an era is the most useful narrowing
            after the phrase, and the catalogue already holds it. */}
        <TextField
          size="small"
          label="Year from"
          value={draft.minYear}
          onChange={(event) => set('minYear', year(event.target.value))}
          sx={{ width: 110 }}
        />
        <TextField
          size="small"
          label="Year to"
          value={draft.maxYear}
          onChange={(event) => set('maxYear', year(event.target.value))}
          sx={{ width: 110 }}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {WORDING_HINT}
      </Typography>
    </Stack>
  );
}
